/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import '@mui/lab';
import { FormControlLabel } from "@mui/material";
import { TreeItem } from '@mui/x-tree-view';
import { ARGOS_PENDING_COLOR, ARGOS_PROGRESS_COLOR } from "../../../../../../../../constants/colors";
import ArgosFlexbox from "../../../../../../../components/Containers/ArgosFlexbox";
import ArgosCheckbox from "../../../../../../../components/Form/Checkbox/ArgosCheckbox";
import { EDatasetExecutionStatus, MERGED_DATASET_TREE_TYPE, MERGED_DATASET_TYPE_STR, SINGLE_DATASET_TREE_TYPE, } from "../../../../constants";
import { ITreeNode } from './CoverageRequirementsTreeComponent';

interface ICustomTreeItemProps {
  nodes: ITreeNode
  treeType: string
  _treeData: any
  renderTreeItemLabel: any
  treeNodeOnClick: any
  treeNodeOnContextMenu: any
  renderTree: any
  checkedNodes: ITreeNode[]
  onChangeChangedNodes: any
}

const enum ECoverageRequirementsNodeType {
  DATASET = "dataset",
  FOLDER = "folder",
}

const EXECUTION_COLOR_MAPPING: any = {
  [EDatasetExecutionStatus.IN_PROGRESS]: ARGOS_PROGRESS_COLOR,
  [EDatasetExecutionStatus.ABORTED]: 'red',
  [EDatasetExecutionStatus.COMPLETED]: "green",
  [EDatasetExecutionStatus.NOT_STARTED]: "",
  [EDatasetExecutionStatus.INVALID_RESULTS]: "orange",
  [EDatasetExecutionStatus.PENDING]: ARGOS_PENDING_COLOR,
}

const CustomTreeItem = (props: ICustomTreeItemProps) => {
  const {
    nodes, treeType, _treeData,
    renderTreeItemLabel, treeNodeOnClick, treeNodeOnContextMenu, renderTree,
    checkedNodes: checkedNodeIds, onChangeChangedNodes
  } = props

  const getTreeLabel = (_treeType: string, _nodes: any) => {
    if (_treeType === SINGLE_DATASET_TREE_TYPE || _treeType === MERGED_DATASET_TREE_TYPE) {
      let showCheckbox = true
      if(_treeType === MERGED_DATASET_TREE_TYPE && _nodes?.meta?.type !== 'root'){
        showCheckbox = false
      }
      if (showCheckbox) {
        return (
          <FormControlLabel
            control={
              <ArgosCheckbox
                hidden={!showCheckbox}
                checked={checkedNodeIds?.some((checkedNode: ITreeNode) => checkedNode.nodeId === nodes.nodeId)}
                onChange={event => onChangeChangedNodes(event.currentTarget.checked, nodes)}
                onClick={e => {
                  e.stopPropagation()
                }}
              />
            }
            label={renderTreeItemLabel(_nodes)}
            key={_nodes?.nodeId}
            onClick={e => e.preventDefault()}
          />
        )
      } else {
        return (
          <ArgosFlexbox
            onClick={e => e.preventDefault()}
          >
            {renderTreeItemLabel(_nodes)}
          </ArgosFlexbox>
        )
      }
    } else if (_treeType === MERGED_DATASET_TYPE_STR) {
      return renderTreeItemLabel(_nodes)
    }
  }

  if (nodes == null || _treeData == null) return null
  const treeItemColor = getTreeItemColor(nodes).treeItemColor
  return (
    <TreeItem
      key={nodes?.nodeId}
      itemId={nodes?.nodeId}
      label={getTreeLabel(treeType, nodes)}
      style={{
        color: treeItemColor?.length ? treeItemColor : 'black',
        whiteSpace: "nowrap",
        width: "fit-content"
      }}
      onClick={(e) => {
        treeNodeOnClick(e, nodes, treeType, _treeData)
      }}
      onContextMenu={(e) => {
        treeNodeOnContextMenu(e, nodes, treeType, _treeData, MERGED_DATASET_TYPE_STR)
      }}
    >
      {
        Array.isArray(nodes?.children)
          ? nodes?.children.map((node: any, index: number) =>
            <CustomTreeItem
              key={node?.nodeId ?? index}
              nodes={node}
              treeType={treeType}
              _treeData={_treeData}
              renderTreeItemLabel={renderTreeItemLabel}
              treeNodeOnClick={treeNodeOnClick}
              treeNodeOnContextMenu={treeNodeOnContextMenu}
              renderTree={renderTree}
              checkedNodes={checkedNodeIds}
              onChangeChangedNodes={onChangeChangedNodes}
            />
          )
          : null
      }
    </TreeItem>
  )

}
export default CustomTreeItem;


export const getTreeItemColor = (_nodes: ITreeNode): {treeItemColor: any, executionStatus: EDatasetExecutionStatus} => {
  let _executionStatus: EDatasetExecutionStatus = EDatasetExecutionStatus.EMPTY
  // console.log('nodes2', _nodes)
  if (_nodes?.nodeType == ECoverageRequirementsNodeType.DATASET && _nodes?.executionStatus?.length) {
    // if(_nodes?.name == "f1") console.log(_nodes)
    _executionStatus = _nodes.executionStatus
  } else {
    const allChildExecutionStatusList = getAllChildExecutionStatusList(_nodes)
    // if(_nodes?.name == "f1") console.log(allChildExecutionStatusList)
    if (allChildExecutionStatusList?.length) {
      if (allChildExecutionStatusList.every((status: string) => status == EDatasetExecutionStatus.COMPLETED)) {
        _executionStatus = EDatasetExecutionStatus.COMPLETED
      } else if (allChildExecutionStatusList.some((status: string) => status == EDatasetExecutionStatus.IN_PROGRESS)) {
        _executionStatus = EDatasetExecutionStatus.IN_PROGRESS
      } else if (allChildExecutionStatusList.some((status: string) => status == EDatasetExecutionStatus.PENDING)) {
        _executionStatus = EDatasetExecutionStatus.PENDING
      } else if (allChildExecutionStatusList.some((status: string) => status == EDatasetExecutionStatus.ABORTED)) {
        _executionStatus = EDatasetExecutionStatus.ABORTED
      } else if (allChildExecutionStatusList.some((status: string) => status == EDatasetExecutionStatus.INVALID_RESULTS)) {
        _executionStatus = EDatasetExecutionStatus.INVALID_RESULTS
      } else if (allChildExecutionStatusList.some((status: string) => status == EDatasetExecutionStatus.NOT_STARTED)) {
        _executionStatus = EDatasetExecutionStatus.NOT_STARTED
      } else {
        _executionStatus = EDatasetExecutionStatus.NOT_STARTED
      }
    }
  }
  const _treeItemColor = _executionStatus?.length ? EXECUTION_COLOR_MAPPING[_executionStatus] : EDatasetExecutionStatus.EMPTY
  return {treeItemColor: _treeItemColor, executionStatus: _executionStatus}
}

const getAllChildExecutionStatusList = (nodes: ITreeNode | null) => {
  if (nodes === null) return []
  const array: any = (nodes?.nodeType == ECoverageRequirementsNodeType.DATASET && nodes?.executionStatus?.length) ? [nodes.executionStatus] : []
  if (Array.isArray(nodes.children)) {
    nodes.children.forEach(node => {
      array.push(...getAllChildExecutionStatusList(node))
      array.filter((v: any, i: number) => array.indexOf(v) === i)
    })
    // only get the first level of children
    // nodes.children.forEach(node => {
    //   array.push(node.executionStatus)
    // })
  }
  // console.log(nodes, array)
  return array;
}